import React, {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useContext,
} from 'react';
import { dfReducer } from './reducer';
import { getConfig } from '../../utils/config';
import { CACHE_TIME, PER_PAGE } from '../../constants';
import { DFContext, type IContext } from '../df-context';
import { initialResultsState } from '../state';
import { search } from '../../api/search';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  BOOKMARKS_SET,
  SET_AVAILABLE_FILTERS,
  SET_EXPANDED_CARD,
  SET_SELECTED,
  UPDATE_STATE,
} from '../actions';
import { useLoader } from '../../hooks/use-loader';
import { useLogin } from '../../hooks/use-login';
import { readBookmarksFromStorage } from '../../utils/bookmark';
import type { IDownloadGroup, IProductData } from '../../types';
import { ErrorContext } from '../../api/error-boundary';
import { useInitAttributes } from '../../hooks/use-init-attributes';
import { useAvailableFilters } from '../../hooks/use-available-filters';

export function DfResultsProvider({
  children,
  dfProductDom,
  productData,
}: {
  children: ReactNode;
  dfProductDom?: Element;
  productData?: IProductData;
}): ReactElement {
  const [state, dispatch] = useReducer(dfReducer, {
    ...initialResultsState,
    preFilter: productData?.prefilter || null,
  });
  const { shareIds, selected, term, itemFilter, preFilter } = state;
  const pimLanguage = getConfig('pim_language', 'en');
  const cmsNativeLanguage = getConfig('cms_native_language', 'EN-EN');
  const setLoginStatus = useLogin(dispatch);
  const errorContext = useContext(ErrorContext);
  const initDownloadAttributes = useInitAttributes(pimLanguage, dispatch);
  const [availableFilters] = useAvailableFilters(productData);

  const updateBookmarks = () => {
    const newBookmarks = readBookmarksFromStorage();
    dispatch({
      type: BOOKMARKS_SET,
      bookmarks: newBookmarks,
    });
  };

  const setSelected = (depth: number, id: string | number) => {
    dispatch({
      type: SET_SELECTED,
      depth,
      id: typeof id === 'string' ? parseInt(id, 10) : id,
    });
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, error, isError } =
    useInfiniteQuery({
      // use different query key when shareIds exist
      queryKey: ['search', term, selected, itemFilter, shareIds.join('-'), preFilter].filter(
        Boolean,
      ),
      queryFn: async ({ pageParam }) => {
        if (dfProductDom && !preFilter) {
          return;
        }

        return search({
          cmsNativeLanguage,
          pimLanguage,
          term,
          params: Object.entries(selected).map(([depth, pimId]) => ({
            depth: parseInt(depth, 10),
            pimId,
          })),
          filterType: itemFilter?.fileType,
          filterLanguage: itemFilter?.language,
          groupIds: shareIds,
          offset: pageParam * PER_PAGE,
          rows: PER_PAGE,
          searchObject: preFilter,
          filterVariant: itemFilter?.variants,
        });
      },
      initialPageParam: 0,
      staleTime: Infinity,
      gcTime: CACHE_TIME,
      retry: false,
      getNextPageParam: (lastPage, allPages) =>
        (lastPage?.data?.searchResult?.numFound || 0) >
        (lastPage?.data?.searchResult?.offset || allPages.length * PER_PAGE)
          ? allPages.length
          : undefined,
    });

  const [newstate, setNewState] = useState<any>(state);

  useEffect(() => {
    if (isError) {
      errorContext?.setError(error.message);
    }
  }, [error, isError, errorContext]);

  useEffect(() => {
    if (data?.pages && data.pages.filter(Boolean).length > 0) {
      const lastPage = data.pages.at(-1);
      const resultKey = dfProductDom ? 'productPageResults' : 'results';

      setNewState({
        [resultKey]: data.pages.reduce((previous: Array<IDownloadGroup>, current) => {
          if (current?.data) {
            previous.push(...current.data.searchResult.searchResult);
          }
          return previous;
        }, []),
        totalDownloads: lastPage?.data?.searchResult?.numFound || 0,
        selectionAmount: lastPage?.data?.searchResult?.numFound || 0,
        countedCategories: lastPage?.data?.categoryFilterResult.countedCategories || [],
        filterAttributes: lastPage?.data?.categoryFilterResult.filter || {
          dfAttributeDfFileType: {},
          dfAttributeDfLanguage: {},
        },
      });
    }
  }, [data, dfProductDom]);

  useEffect(() => {
    if (availableFilters) {
      dispatch({
        type: SET_AVAILABLE_FILTERS,
        value: availableFilters,
      });
    }
  }, [availableFilters]);

  useEffect(() => {
    updateBookmarks();
    initDownloadAttributes();

    // sync app state when several browser tabs are opened
    window.addEventListener('storage', updateBookmarks);
    return () => {
      window.removeEventListener('storage', updateBookmarks);
    };
  }, [initDownloadAttributes]);

  useEffect(() => {
    if (shareIds.length === 1) {
      dispatch({
        type: SET_EXPANDED_CARD,
        card: shareIds[0],
      });
    }
  }, [shareIds]);

  const showLoader = useLoader(isLoading);

  const updateState = useCallback((newState: any) => {
    dispatch({
      type: UPDATE_STATE,
      ...newState,
    });
  }, []);

  const value = useMemo(
    () => ({
      state: {
        ...state,
        ...newstate,
        loadMore: hasNextPage,
        isLoading: showLoader,
        isFetching,
      },
      dispatch,
      setSelected,
      fetchNextPage,
      setLoginStatus,
      updateState,
    }),
    [
      fetchNextPage,
      hasNextPage,
      isFetching,
      newstate,
      setLoginStatus,
      showLoader,
      state,
      updateState,
    ],
  );

  return <DFContext.Provider value={value as IContext}>{children}</DFContext.Provider>;
}
